/**
 * Star Rating Component
 * Displays star ratings with filled, half-filled, and empty states
 */

import styled from '@emotion/styled';

const StarsContainer = styled.div<{ size?: 'sm' | 'md' | 'lg' }>`
  display: flex;
  align-items: center;
  gap: 2px;
  
  svg {
    width: ${({ size }) => {
      switch (size) {
        case 'sm': return '14px';
        case 'lg': return '24px';
        default: return '18px';
      }
    }};
    height: ${({ size }) => {
      switch (size) {
        case 'sm': return '14px';
        case 'lg': return '24px';
        default: return '18px';
      }
    }};
  }
`;

interface StarRatingProps {
  rating: number;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
  className?: string;
}

export function StarRating({ 
  rating, 
  maxRating = 5, 
  size = 'md',
  showValue = false,
  className 
}: StarRatingProps) {
  const stars = [];
  
  for (let i = 1; i <= maxRating; i++) {
    if (i <= Math.floor(rating)) {
      // Full star
      stars.push(
        <svg key={i} viewBox="0 0 24 24" fill="#FDB913">
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      );
    } else if (i === Math.ceil(rating) && rating % 1 !== 0) {
      const fraction = rating - Math.floor(rating);
      const pct = `${Math.round(fraction * 100)}%`;
      stars.push(
        <svg key={i} viewBox="0 0 24 24">
          <defs>
            <linearGradient id={`partial-star-${i}`}>
              <stop offset={pct} stopColor="#FDB913" />
              <stop offset={pct} stopColor="#E0E0E0" />
            </linearGradient>
          </defs>
          <path 
            d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" 
            fill={`url(#partial-star-${i})`}
          />
        </svg>
      );
    } else {
      stars.push(
        <svg key={i} viewBox="0 0 24 24" fill="#E0E0E0">
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      );
    }
  }

  return (
    <StarsContainer size={size} className={className}>
      {stars}
      {showValue && (
        <span style={{ marginLeft: '8px', fontWeight: 600 }}>
          {rating.toFixed(1)}
        </span>
      )}
    </StarsContainer>
  );
}

